import React, { useRef, useState } from 'react';
import CKEditor from 'ckeditor4-react';
import { Labeled, useInput, InputHelperText } from 'react-admin';
import { Field } from 'react-final-form';
import { FormHelperText, FormControl } from '@material-ui/core';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import red from '@material-ui/core/colors/red';
import classNames from 'classnames';
import FieldTitleWithListPopover from './FieldTitleWithListPopover';

const styles = () => ({
  labelError: {
    color: red[500] + ' !important'
  },
  value: {
    padding: '0px !important'
  },
  inputTitleContainer: {
    width: '100%',
    display: 'flex',
    '& .MuiGrid-root': {
      flex: 1
    }
  },
  labelContainer: {
    width: '100%'
  },
  label: {
    minHeight: '20px'
  }
});

const useStyles = makeStyles(styles);

/**
 * Rich Text Editor input with CKEditor 4
 * https://ckeditor.com/docs/ckeditor4/latest/
 */
const RichTextInput = props => {
  const {
    label,
    source,
    editorConfig,
    classes: classesOverride,
    variables,
    displayingVariables,
    ListPopoverProps,
    disabled
  } = props;
  const [mode, setMode] = useState('wysiwyg');
  const classes = useStyles({ classes: classesOverride });
  const editorRef = useRef(null);
  const {
    input: { onChange, value },
    isRequired,
    meta: { touched, error, ...meta }
  } = useInput(props);

  function handleChange(evt) {
    onChange(evt.editor.getData());
  }

  function handleSelectVariable(variable) {
    if (variable.value === '!documentUrl') {
      editorRef.current.editor.insertHtml(
        `<a href="${variable.value}"> !documentUrl </a>`
      );
    } else {
      editorRef.current.editor.insertText(variable.value);
    }
  }
  return (
    <FormControl error={!!(touched && error)} margin="dense" fullWidth>
      <Labeled
        label={
          <FieldTitleWithListPopover
            onSelectItem={handleSelectVariable}
            items={variables}
            source={source}
            label={label}
            isRequired={isRequired}
            displayingVariables={displayingVariables && mode === 'wysiwyg'}
            ListPopoverProps={ListPopoverProps}
          />
        }
        meta={meta}
        classes={{
          label: classNames(
            classes.label,
            !!(touched && error) && classes.labelError
          ),
          value: classes.value
        }}
      >
        <Field name={source}>
          {() => (
            <CKEditor
              ref={editorRef}
              data={value}
              onChange={handleChange}
              onMode={({ editor }) => setMode(editor.mode)}
              config={editorConfig}
              // see https://github.com/ckeditor/ckeditor4-react/issues/57
              onBeforeLoad={CKEDITOR => (CKEDITOR.disableAutoInline = true)}
              readOnly={disabled}
            />
          )}
        </Field>
      </Labeled>
      {touched && error ? (
        <FormHelperText error={!!error}>
          <InputHelperText error={error} helperText={true} touched={touched} />
        </FormHelperText>
      ) : null}
    </FormControl>
  );
};

RichTextInput.propTypes = {
  editorConfig: PropTypes.object, // Configuration options for CKEditor 4
  className: PropTypes.string,
  source: PropTypes.string,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  displayingVariables: PropTypes.bool,
  variables: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string
    })
  ),
  ListPopoverProps: PropTypes.object
};

RichTextInput.defaultProps = {
  displayingVariables: true,
  disabled: false,
  editorConfig: {
    toolbar: [
      ['Source', 'NewPage'],
      ['Cut', 'Copy', 'Paste', 'PasteText', 'PasteFromWord'],
      ['Undo', 'Redo'],
      ['Find', 'Replace', 'SelectAll', 'Scayt'],
      ['NumberedList', 'BulletedList', 'Outdent', 'Indent', 'Blockquote'],
      [
        'JustifyLeft',
        'JustifyCenter',
        'JustifyRight',
        'JustifyBlock',
        'BidiLtr',
        'BidiRtl'
      ],
      ['Table', 'HorizontalRule', 'Smiley', 'SpecialChar'],
      '/',
      ['Bold', 'Italic', 'Underline', 'Strike', 'Subscript', 'Superscript'],
      ['RemoveFormat'],
      ['Styles'],
      ['Format'],
      ['Font'],
      ['FontSize'],
      ['TextColor', 'BGColor'],
      ['Link', 'Unlink'],
      ['Maximize', 'ShowBlocks']
    ],
    allowedContent: true
  },
  variables: []
};

export default RichTextInput;
